import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Breadcrumb, Button, Dropdown } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { BiDownload, BiLogOut } from 'react-icons/bi';
import { BsBoxArrowLeft, BsFileEarmarkPdf, BsFileWord, BsThreeDotsVertical, BsXLg } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HTMLEditor from '../../../../components/Editor/HTMLEditor';
import AITextGenWithPrompt from '../../../../components/AI/AITextGenWithPrompt';
import { ReportSection } from '../../../../core/constants/common';
import { generateBRSRPDFFromReport, generateBRSRWordFromReport } from '../../../../core/utilities/BRSRUtils';
import ReportingUtils, { syncBrsrNumericCellAlignment } from '../../../../core/utilities/ReportingUtils';
import { buildReportingListUrl } from '../../../../core/utilities/reportingListUrl';
import esg_ai from '../../../../images/esg_ai_Green.png';
import { beautifyReportHtml } from '../utils/htmlBeautifier';
import './../Reporting.scss';
import './BRSR.scss';
import { LoaderContext } from '../../../../App';

const BRSRReportingCard = ({
	t,
	reportName,
	page,
	section,
	onSave,
	onFinalize,
	onCancel,
	onContentEdited,
	reportId,
	report,
	yearFilter,
	regulationId,
	regulationName,
	regulationFrequency,
	reportFrequency,
	jobId,
	loader,
	periodStart,
	periodEnd,
	entities,
	language,
	getContentRef,
	activeTab,
	listPage,
	listSize
}) => {

	const [edited, setEdited] = useState(false);
	const [saveEnabled, setSaveEnabled] = useState(false);
	const [pageHTML, setPageHTML] = useState(page?.html || '');
	const [showFullView, setShowFullView] = useState(true);
	const [showAITextGen, setShowAITextGen] = useState(true);
	const [showCopilotHistory, setShowCopilotHistory] = useState(false);
	const [periodYear, setPeriodYear] = useState(yearFilter);
	const editorRef = useRef(null);
	const navigate = useNavigate();

	const goToReportingList = () => {
		navigate(buildReportingListUrl({
			view: 'BRSR',
			page: activeTab,
			listPage,
			size: listSize,
		}));
	};
	const cardScrollRef = useRef(null);
	const brsrPreviewRef = useRef(null);
	const rightPanelRef = useRef(null);
	const [panelWidth, setPanelWidth] = useState(400);
	const { setLoaderDisplay } = useContext(LoaderContext);

	useEffect(() => {
		setPageHTML(page?.html || '');
	}, [page?.html]);

	useEffect(() => {
		if (!getContentRef) {
			return undefined;
		}
		getContentRef.current = () => ({
			html: edited && editorRef?.current ? editorRef.current.getContent() : pageHTML,
			json: page.json || null
		});
		return () => {
			getContentRef.current = null;
		};
	}, [edited, pageHTML, page?.json, getContentRef]);

	useEffect(() => {
		setPeriodYear(yearFilter);
	}, [yearFilter]);

	useEffect(() => {
		if (!showFullView && rightPanelRef.current && panelWidth) {
			rightPanelRef.current.style.width = `${panelWidth}px`;
		}
	}, [showFullView, panelWidth]);

	const transferTextToEditor = (text, jsonData = null) => {
		setEdited(true);
		onContentEdited();	
		let htmlString = '';
		if (text && typeof text === 'string' && text.includes('<p><em>')) {
			htmlString = text;
		} else if (text && typeof text === 'string' && text.includes('container-fluid') && text.includes('flex-wrap')) {
			htmlString = text;
		} else if (jsonData) {
			htmlString = ReportingUtils.getESGHighlightsCardBasedHTML(jsonData);
		} else {
			htmlString = ReportingUtils.getHTMLWithTextFormattedAsParagraphs({ text: text || '' });
		}
		setSaveEnabled(true);
		if (edited && editorRef?.current) {
			setPageHTML(editorRef.current.insertContent(htmlString));
		} else {
			setPageHTML((prev) => prev + htmlString);
		}
	};

	// Show copilot for all BRSR content sections except title, contents, leadership (same pattern as GRI)
	const canShowCopilot = () => {
		if (!section) return true;
		return section.type !== ReportSection.TITLE
			&& section.type !== ReportSection.CONTENTS
			&& section.type !== ReportSection.LEADERSHIP;
	};

	const handleResizeMouseDown = (e) => {
		e.preventDefault();
		document.addEventListener('mousemove', handleResizeMouseMove);
		document.addEventListener('mouseup', handleResizeMouseUp);
	};

	const handleResizeMouseMove = (e) => {
		const panel = rightPanelRef.current;
		if (!panel) return;
		const newWidth = panel.getBoundingClientRect().right - e.clientX;
		const maxWidth = 575;
		const minWidth = 200;
		const calculatedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
		setPanelWidth(calculatedWidth);
		panel.style.width = `${calculatedWidth}px`;
	};

	const handleResizeMouseUp = () => {
		document.removeEventListener('mousemove', handleResizeMouseMove);
		document.removeEventListener('mouseup', handleResizeMouseUp);
	};

	useEffect(() => {
		if (cardScrollRef.current) {
			const rect = cardScrollRef.current.getBoundingClientRect();
			const headerOffset = 120;
			const targetTop = window.scrollY + rect.top - headerOffset;
			window.scrollTo({ top: targetTop, behavior: 'smooth' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page?.id, page?.index]);

	useLayoutEffect(() => {
		if (edited) return;
		const root = brsrPreviewRef.current;
		if (!root) return;
		syncBrsrNumericCellAlignment(root);
	}, [edited, pageHTML]);

	const createMarkup = () => {
		return { __html: pageHTML };
	};

	const onCardClick = () => {
		setEdited(true);
	};

	const handleSave = () => {
		const htmlData = edited && editorRef?.current ? editorRef.current.getContent() : pageHTML;
		onSave(htmlData, page.json || null);
		setEdited(false);
		setSaveEnabled(false);
	};

	const handleBeautify = () => {
		try {
			const currentHtml = (editorRef?.current?.getContent() ?? pageHTML) || '';
			const beautified = beautifyReportHtml(currentHtml);
			editorRef?.current?.setContent(beautified);
			setPageHTML(beautified);
			setSaveEnabled(true);
			onContentEdited();
			toast.success(t("Beautify.success.label"));
		} catch (err) {
			toast.error(t("Beautify.error.label"));
		}
	};

	const handleFinalize = () => {
		const htmlData = edited && editorRef?.current ? editorRef.current.getContent() : pageHTML;
		onFinalize(htmlData, page.json || null);
		setEdited(false);
		setSaveEnabled(false);
	};

	const handleCancel = () => {
		setEdited(false);
		setSaveEnabled(false);
		setPageHTML(page.html || '');
		onCancel();
	};

	const getCardActions = () => {
		return <>
			<div className='floating-action-panel row'>
				<div className='col-md-5'>
					<Breadcrumb>
						<Breadcrumb.Item onClick={goToReportingList}>
							{t("ReportingStudio.label")}
						</Breadcrumb.Item>
						<Breadcrumb.Item active>{reportName}</Breadcrumb.Item>
					</Breadcrumb>
				</div>
				<div className='action-holder RS_btn_head col-md-7'>
					<div className='d-flex'>
						{!edited && reportId > 0 && (
							// <Dropdown>
							// 	<Dropdown.Toggle variant="light" id="dropdown-basic" className='btn me-2'>
							// 		<BiDownload /> {t("Download.label")}
							// 	</Dropdown.Toggle>
							// 	<Dropdown.Menu className="CustomizeColumns_drop p-2">
							// 		<Dropdown.Item onClick={() => {
							// 			generateBRSRWordFromReport(report, setLoaderDisplay, (error) => {
							// 				toast.error(error.message || 'Error generating Word document', {
							// 					position: toast.POSITION.TOP_RIGHT,
							// 				});
							// 			});
							// 		}}><BsFileWord /> {t("MSWord.label")}</Dropdown.Item>
							// 		<Dropdown.Item onClick={() => {
							// 			generateBRSRPDFFromReport(report, setLoaderDisplay, (error) => {
							// 				toast.error(error.message || 'Error generating PDF', {
							// 					position: toast.POSITION.TOP_RIGHT,
							// 				});
							// 			});
							// 		}}><BsFileEarmarkPdf /> {t("Pdf.label")}</Dropdown.Item>
							// 	</Dropdown.Menu>
							// </Dropdown>
							<button className='btn btn-light me-2'
								onClick={() => {
									generateBRSRPDFFromReport(report, setLoaderDisplay, (error) => {
										toast.error(error.message || 'Error generating PDF', {
											position: toast.POSITION.TOP_RIGHT,
										});
									});
								}}><BsFileEarmarkPdf /> {t("Preview.label")}
							</button>
						)}
						{edited && (
							<>
								<button className='btn btn-outline-secondary me-2'
									onClick={handleBeautify}>
									{t("Beautify.label")}
								</button>
								<button className='btn btn-primary me-2'
									onClick={handleSave}
									disabled={!saveEnabled}>
									{t("Save.label")}
								</button>
								<button className='btn btn-secondary me-2'
									onClick={handleCancel}>
									{t("Cancel.label")}
								</button>
								{reportId > 0 && (
									<button className='btn btn-success me-2'
										onClick={handleFinalize}
										disabled={!saveEnabled}>
										{t("Finalize.label")}
									</button>
								)}
							</>
						)}
						{showFullView && canShowCopilot() && (
							<button className='btn btn-secondary me-2 px-2'
								onClick={() => setShowFullView(false)}>
								{t("ESGAICopilot.label")}
							</button>
						)}
						<button className='btn btn-primary'
							onClick={goToReportingList}>
							<BiLogOut /> {t("Exit.label")}
						</button>
					</div>
				</div>
			</div>
		</>;
	};

	const getPagetoRender = () => {
		return <>
			<div className='Reporting_CardBox BRSR_Card_report  RC_Commen_card HtmlEditorBox 1' onClick={() => onCardClick()}>
				<div className='RC_Commen_card_inner'>
					{
						edited
							? <HTMLEditor editorRef={editorRef} initialValue={pageHTML}
								onContentChange={(e) => {
									setSaveEnabled(true);
									onContentEdited();
								}} />
							: <div ref={brsrPreviewRef} dangerouslySetInnerHTML={createMarkup()}></div>
					}
				</div>
			</div>
		</>;
	};

	return (
		<>
			<div className='floating-actionSticky col-md-12 px-1 pb-2'>
				{getCardActions()}
			</div>
			<div className='col-md-12 ReportfileAndSection p-0 3'>
				<div
					ref={cardScrollRef}
					className={showFullView ? 'col-md-12 px-1' : 'ReportfileAndSectionIn02'}
				>
					{getPagetoRender()}
				</div>

				{canShowCopilot() && (
					<div
						className={showFullView ? 'd-none' : 'RightSide_Tab AITextGenWithPrompt_tab'}
						ref={rightPanelRef}
						style={{ width: `${panelWidth}px` }}
					>
						<div className="resize-handle-left" onMouseDown={handleResizeMouseDown} />
						<div className='RightSide_Tab_head'>
							<div className='RightSide_Tab_head_in'>
								<div className='RTH_01esg'>
									<img src={esg_ai} width={15} height={15} alt="ESG AI" />
									<h6 className='mb-0'>{t('ESGAICopilot.label')}</h6>
								</div>
								<div className='AIText_menu'>
									<Dropdown>
										<Dropdown.Toggle variant="AIGen_dotMenu" id="dropdown-basic">
											<BsThreeDotsVertical />
										</Dropdown.Toggle>
										<Dropdown.Menu>
											<Dropdown.Item href="https://ecodrisil.com/privacy-policy/" onClick={(e) => e.preventDefault()}>Privacy</Dropdown.Item>
											<Dropdown.Item href="https://ecodrisil.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms & Conditions</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>
									<span className='cursor' onClick={() => setShowFullView(true)}>
										<BsXLg />
									</span>
								</div>
								{loader && (
									<div className='spinner-Body'>
										<div className='spinner-8'></div>
									</div>
								)}
							</div>
						</div>
						<div>
							<AITextGenWithPrompt
								page={page}
								section={section}
								periodYear={periodYear}
								regulationId={regulationId}
								regulationName={regulationName}
								regulationFrequency={regulationFrequency}
								onHide={() => setShowAITextGen(false)}
								onTransferToEditor={transferTextToEditor}
								jobId={jobId}
								reportingStandard="BRSR"
								language={language}
								copilotHistory={showCopilotHistory}
								hideCopilotHistory={(e) => setShowCopilotHistory(e)}
								reportName={reportName}
								periodStart={periodStart}
								periodEnd={periodEnd}
								entities={entities}
								report={report ? { name: report.name, basis: report.type } : undefined}
							/>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default withTranslation()(BRSRReportingCard);
